import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RotateCcw, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { EV, isLeaderboardGameId, type ResultRow } from '@puzzle-arena/shared';
import { PixelButton, PixelPanel } from '../ui/primitives.js';
import { SeatAvatar } from '../ui/game-bits.js';
import { PODIUM_STEP_MS, RESULT_ROW_MS, stepTransition, useReducedMotion } from '../ui/motion.js';
import { seatColor } from '../ui/seat.js';
import { api, emit, useRoom } from '../net/socket.js';
import { MastermindSecretReveal } from '../games/MastermindBoard.js';
/** The shape of `RoomStore.state` this page actually reads — a live game's
 * public state carries a lot more, but the reveal only ever needs these two. */
interface MastermindRevealState {
  solution?: { code?: number[] };
  puzzle?: { colors?: number };
}

export default function ResultsPage(): React.ReactElement {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = React.useState<ResultRow[] | null>(null);
  const [notFound, setNotFound] = React.useState(false);
  const [rematching, setRematching] = React.useState(false);
  const [gameId, setGameId] = React.useState<string | null>(null);

  React.useEffect(() => {
    void (async () => {
      const cached = useRoom.getState();
      const storeResults = cached.room?.code === code.toUpperCase() ? cached.results : null;
      if (storeResults && storeResults.length > 0) {
        setResults(storeResults);
      }
      if (cached.room?.code === code.toUpperCase() && cached.room.gameId) {
        setGameId(cached.room.gameId);
      }
      const lookup = await api<{ id: string; gameId: string }>(`/api/rooms/${code.toUpperCase()}`);
      if (lookup.status !== 200) {
        if (!storeResults || storeResults.length === 0) setNotFound(true);
        return;
      }
      setGameId(lookup.body.gameId);
      const res = await api<{ results: ResultRow[] }>(`/api/rooms/${lookup.body.id}/results`);
      if (res.status !== 200) {
        if (!storeResults || storeResults.length === 0) setNotFound(true);
        return;
      }
      setResults(res.body.results ?? []);
    })();
  }, [code]);

  const isLeaderboardGame = gameId !== null && isLeaderboardGameId(gameId);



  const store = useRoom();
  const roomMatches = store.room?.code === code.toUpperCase();
  const state = roomMatches ? (store.state as MastermindRevealState | null) : null;
  const isMastermind =
    (roomMatches && store.room?.gameId === 'mastermind') || results?.some((r) => getMastermindDetail(r.detail) !== null);
  const isHost = roomMatches && store.you?.isHost === true;
  React.useEffect(() => {
    if (roomMatches && store.room?.status === 'lobby' && store.results === null) {
      navigate(`/r/${code.toUpperCase()}${store.you?.isHost ? '?host=1' : ''}`, { replace: true });
    }
  }, [code, navigate, roomMatches, store.results, store.room?.status, store.you?.isHost]);

  const playAgain = async (): Promise<void> => {
    if (!isHost || rematching) return;
    setRematching(true);
    const res = await emit<{ ok?: boolean; error?: string }>(EV.roomRestart);
    if (res.error || !res.ok) {
      toast(res.error ?? 'Could not start the rematch');
      setRematching(false);
      return;
    }
    useRoom.getState().reset();
    navigate(`/r/${code.toUpperCase()}?host=1`);
  };


  return (
    <main className="min-h-screen p-4 md:p-8 max-w-4xl mx-auto flex flex-col gap-6">
      <h1 className="font-display text-pa-cyan" style={{ fontSize: 'clamp(20px, 6vw, 32px)' }}>
        RESULTS
      </h1>
      <PixelPanel title={`Room ${code.toUpperCase()}`}>
        {notFound ? (
          <div className="flex flex-col gap-3 items-start">
            <p className="text-pa-ink-dim">Room not found or results have expired.</p>
            <PixelButton onClick={() => navigate('/')}>Back to home</PixelButton>
          </div>
        ) : results ? (
          <ResultsTable results={results} />
        ) : (
          <p className="text-pa-ink-dim">Loading…</p>
        )}
      </PixelPanel>
      {isMastermind && state?.solution?.code && (
        <PixelPanel title="Secret Code Reveal">
          <MastermindSecretReveal
            code={state.solution.code}
            colors={state.puzzle?.colors ?? 8}
          />
        </PixelPanel>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {roomMatches && !isHost && (
          <p className="text-[12px] text-pa-ink-dim">
            Waiting for the host to start another round.
          </p>
        )}
        {isHost && (
          <div>
            <PixelButton
              className="self-start"
              onClick={() => void playAgain()}
              disabled={rematching}
              aria-busy={rematching}
            >
              <RotateCcw size={14} strokeWidth={3} className={rematching ? 'lucide animate-spin' : 'lucide'} />
              {rematching ? 'Starting rematch…' : 'Play Again'}
            </PixelButton>
            {rematching && (
              <p role="status" className="mt-2 text-[12px] text-pa-ink-dim">
                Setting up the same game again…
              </p>
            )}
          </div>
        )}
        <PixelButton
          className="self-start"
          onClick={() => {
            useRoom.getState().reset();
            navigate('/');
          }}
        >
          Back to home
        </PixelButton>
        {isLeaderboardGame && (
          <PixelButton
            variant="secondary"
            onClick={() => navigate(`/leaderboard/${gameId}`)}
          >
            <Trophy size={14} strokeWidth={3} className="lucide" />
            View Global Leaderboard
          </PixelButton>
        )}
        <PixelButton
          variant="secondary"
          onClick={() => {
            useRoom.getState().reset();
            navigate('/admin');
          }}
        >
          Host another room
        </PixelButton>
      </div>
    </main>
  );
}

/** Podium rises 3rd, then 2nd, then 1st; the table reveals row by row. */
function getMastermindDetail(detail: unknown): { tries: number; maxTries: number } | null {
  if (detail && typeof detail === 'object' && 'mastermind' in detail) {
    const mm = (detail as { mastermind: unknown }).mastermind;
    if (mm && typeof mm === 'object' && 'tries' in mm && 'maxTries' in mm) {
      return mm as { tries: number; maxTries: number };
    }
  }
  return null;
}

export function ResultsTable({ results }: { results: ResultRow[] }): React.ReactElement {
  const reduced = useReducedMotion();
  const isMastermind = results.some((r) => getMastermindDetail(r.detail) !== null);
  const podium = [...results].slice(0, 3);
  const order = [2, 1, 0]; // 3rd, 2nd, 1st

  const heights = ['h-32', 'h-24', 'h-16'];

  return (
    <div className="flex flex-col gap-8">
      {podium.length > 0 && (
        <div className="flex items-end justify-center gap-3">
          {[1, 0, 2].map((slot) => {
            const row = podium[slot];
            if (!row) return null;
            const delayIndex = order.indexOf(slot);
            return (
              <motion.div
                key={row.playerId}
                initial={reduced ? {} : { y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  ...stepTransition(reduced ? 0 : PODIUM_STEP_MS),
                  delay: reduced ? 0 : (delayIndex * PODIUM_STEP_MS) / 1000,
                }}
                className="flex flex-col items-center gap-2"
              >
                <SeatAvatar
                  seat={row.seat}
                  displayName={row.displayName}
                  avatar={row.avatar}
                  isBot={row.isBot}
                  size={40}
                />
                <span className="font-display text-[10px] truncate max-w-[6rem]">
                  {row.displayName}
                </span>
                <div
                  className={`w-20 md:w-24 border-2 flex items-start justify-center pt-2 ${heights[slot]}`}
                  style={{
                    borderColor: seatColor(row.seat),
                    backgroundColor: 'var(--color-pa-surface)',
                  }}
                >
                  <span className="font-display text-[16px] tabular">{row.rank}</span>
                </div>
                <span className="font-display text-[12px] tabular text-pa-cyan">{row.score}</span>
              </motion.div>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:hidden">
        {results.map((row, i) => (
          <motion.div
            key={row.playerId}
            initial={reduced ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              ...stepTransition(reduced ? 0 : RESULT_ROW_MS),
              delay: reduced ? 0 : (i * RESULT_ROW_MS) / 1000,
            }}
            className="border-2 border-pa-border bg-pa-surface-2 p-3"
          >
            <div className="flex items-center justify-between gap-2 border-b border-pa-border pb-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="font-display text-[12px] text-pa-ink-dim">{row.rank}.</span>
                <SeatAvatar
                  seat={row.seat}
                  displayName={row.displayName}
                  avatar={row.avatar}
                  isBot={row.isBot}
                  size={24}
                />
                <span className="min-w-0 truncate text-[13px]">{row.displayName}</span>
              </div>
              <span className="shrink-0 font-display text-[16px] tabular text-pa-cyan">{row.score}</span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] tabular">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-pa-ink-dim">Progress</dt>
                <dd>{Math.round(row.progress * 100)}%</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-pa-ink-dim">Accuracy</dt>
                <dd>{Math.round(row.accuracy * 100)}%</dd>
              </div>
              {(() => {
                const mm = getMastermindDetail(row.detail);
                return (
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-pa-ink-dim">{mm ? 'Attempts' : 'Speed'}</dt>
                    <dd>{mm ? `${mm.tries} / ${mm.maxTries}` : `${Math.round(row.speed * 100)}%`}</dd>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between gap-2">
                <dt className="text-pa-ink-dim">Penalties</dt>
                <dd>{row.penalties}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-pa-ink-dim">Finish</dt>
                <dd>
                  {row.completedAtMs === null
                    ? '—'
                    : `${Math.floor(row.completedAtMs / 60000)}:${String(
                        Math.floor((row.completedAtMs % 60000) / 1000),
                      ).padStart(2, '0')}`}
                </dd>
              </div>
            </dl>
          </motion.div>
        ))}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[560px] text-[13px] tabular">
          <thead>
            <tr className="border-b-2 border-pa-border text-pa-ink-dim">
              <th className="text-left py-2 font-normal">#</th>
              <th className="text-left py-2 font-normal">Player</th>
              <th className="text-right py-2 font-normal">Score</th>
              <th className="text-right py-2 font-normal">Progress</th>
              <th className="text-right py-2 font-normal">Accuracy</th>
              <th className="text-right py-2 font-normal">{isMastermind ? 'Attempts' : 'Speed'}</th>
              <th className="text-right py-2 font-normal">Pen.</th>
              <th className="text-right py-2 font-normal">Finish</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row, i) => (
              <motion.tr
                key={row.playerId}
                initial={reduced ? {} : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  ...stepTransition(reduced ? 0 : RESULT_ROW_MS),
                  delay: reduced ? 0 : (i * RESULT_ROW_MS) / 1000,
                }}
                className="border-b border-pa-border"
              >
                <td className="py-2">{row.rank}</td>
                <td className="py-2">
                  <span className="flex items-center gap-2">
                    <SeatAvatar
                      seat={row.seat}
                      displayName={row.displayName}
                      avatar={row.avatar}
                      isBot={row.isBot}
                      size={20}
                    />
                    <span className="truncate max-w-[12rem]">{row.displayName}</span>
                  </span>
                </td>
                <td className="py-2 text-right font-display text-[10px]">{row.score}</td>
                <td className="py-2 text-right">{Math.round(row.progress * 100)}%</td>
                <td className="py-2 text-right">{Math.round(row.accuracy * 100)}%</td>
                <td className="py-2 text-right">
                  {(() => {
                    const mm = getMastermindDetail(row.detail);
                    return mm ? `${mm.tries} / ${mm.maxTries}` : `${Math.round(row.speed * 100)}%`;
                  })()}
                </td>
                <td className="py-2 text-right">{row.penalties}</td>
                <td className="py-2 text-right">
                  {row.completedAtMs === null
                    ? '—'
                    : `${Math.floor(row.completedAtMs / 60000)}:${String(
                        Math.floor((row.completedAtMs % 60000) / 1000),
                      ).padStart(2, '0')}`}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
